import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { RolloutCampaign, CampaignInstallation } from '@hospasense/contracts';
import {
  fetchCampaign,
  pauseCampaign,
  resumeCampaign,
  rollbackCampaign,
} from '../lib/queries-releases.js';
import { ActionReasonModal } from './release-modals.js';
import { useAuth } from '../lib/auth.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Button,
  Card,
  CardBody,
  PageHeader,
  Badge,
  StatusPill,
  Table,
  TableContainer,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingState,
  ErrorState,
} from '../components/ui/index.js';

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const canRollout = hasPermission('release.rollout');

  const [campaign, setCampaign] = useState<RolloutCampaign | null>(null);
  const [installations, setInstallations] = useState<CampaignInstallation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [rollbackModalOpen, setRollbackModalOpen] = useState(false);

  const loadAll = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await fetchCampaign(id);
      setCampaign(data.campaign);
      setInstallations(data.installations);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
  }, [id]);

  const handlePause = async (reason: string) => {
    if (!id) return;
    await pauseCampaign(id, reason);
    await loadAll();
  };

  const handleResume = async () => {
    if (!id) return;
    await resumeCampaign(id);
    await loadAll();
  };

  const handleRollback = async (reason: string) => {
    if (!id) return;
    await rollbackCampaign(id, reason);
    await loadAll();
  };

  if (loading) {
    return <LoadingState label="Loading campaign rollout state..." />;
  }

  if (error || !campaign) {
    return (
      <ErrorState
        description={error ?? 'Campaign not found'}
        onRetry={() => {
          setError(null);
          void loadAll();
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={campaign.name}
        subtitle={`Release version: ${campaign.releaseVersion} • Target Ring: ${campaign.targetRing.toUpperCase()}`}
        breadcrumbs={
          <Breadcrumbs items={[{ label: 'Releases', to: '/releases' }, { label: campaign.name }]} />
        }
        actions={
          canRollout && (
            <div className="flex items-center gap-2">
              {campaign.status === 'in_progress' && (
                <>
                  <Button variant="outline" onClick={() => setPauseModalOpen(true)}>
                    Pause Rollout
                  </Button>
                  <Button variant="danger" onClick={() => setRollbackModalOpen(true)}>
                    Emergency Rollback
                  </Button>
                </>
              )}

              {campaign.status === 'paused' && (
                <>
                  <Button variant="primary" onClick={handleResume}>
                    Resume Rollout
                  </Button>
                  <Button variant="danger" onClick={() => setRollbackModalOpen(true)}>
                    Rollback
                  </Button>
                </>
              )}
            </div>
          )
        }
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card>
          <CardBody>
            <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
              Version & Ring
            </span>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-mono text-base font-bold text-text-primary">
                {campaign.releaseVersion}
              </span>
              <Badge tone="accent">{campaign.targetRing}</Badge>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
              Campaign Status
            </span>
            <div className="mt-1">
              <StatusPill
                tone={
                  campaign.status === 'completed'
                    ? 'success'
                    : campaign.status === 'in_progress'
                      ? 'info'
                      : campaign.status === 'paused'
                        ? 'warning'
                        : 'error'
                }
                label={campaign.status.replace(/_/g, ' ')}
              />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
              Health Gate
            </span>
            <div className="mt-1 text-xs text-text-secondary">
              <div>
                Max Error:{' '}
                <strong className="text-text-primary">
                  {campaign.healthGate.maxErrorRatePercent}%
                </strong>
              </div>
              <div>
                Auto Rollback:{' '}
                <strong className="text-text-primary">
                  {campaign.healthGate.autoRollbackOnFailure ? 'Enabled' : 'Disabled'}
                </strong>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
              Target Progress
            </span>
            <div className="mt-1 text-xs text-text-secondary">
              <div className="tabular-nums">
                {campaign.updatedInstallationsCount} of {campaign.targetInstallationsCount} nodes
                updated
              </div>
              {campaign.failedInstallationsCount > 0 && (
                <div className="text-error font-semibold tabular-nums">
                  {campaign.failedInstallationsCount} failed
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      {campaign.pausedReason && (
        <div className="rounded-xl border border-border bg-warning-bg p-4 text-xs text-warning">
          <strong>Rollout Paused:</strong> {campaign.pausedReason}
        </div>
      )}

      {campaign.rollbackReason && (
        <div className="rounded-xl border border-border bg-error-bg p-4 text-xs text-error">
          <strong>Campaign Rolled Back:</strong> {campaign.rollbackReason}
        </div>
      )}

      {/* Target Nodes Table */}
      <Card>
        <CardBody>
          <h2 className="text-sm font-bold uppercase tracking-wider text-text-secondary">
            Target Installations in Ring ({campaign.targetRing})
          </h2>
          <div className="mt-4">
            {installations.length === 0 ? (
              <p className="text-xs text-text-muted">
                No installations assigned to this rollout campaign yet.
              </p>
            ) : (
              <TableContainer>
                <Table density="compact">
                  <THead>
                    <TR>
                      <TH>Installation ID</TH>
                      <TH>Ring</TH>
                      <TH>Deployment Status</TH>
                      <TH>Updated / Error</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {installations.map((ci) => (
                      <TR key={ci.id}>
                        <TD className="font-mono">
                          <Link to={`/fleet/${ci.installationId}`} className="hover:underline">
                            {ci.installationId}
                          </Link>
                        </TD>
                        <TD>
                          <Badge tone="neutral">{ci.rolloutRing}</Badge>
                        </TD>
                        <TD>
                          <StatusPill
                            tone={
                              ci.status === 'dispatched'
                                ? 'success'
                                : ci.status === 'in_progress'
                                  ? 'info'
                                  : ci.status === 'pending'
                                    ? 'warning'
                                    : 'error'
                            }
                            label={ci.status}
                          />
                        </TD>
                        <TD>
                          {ci.errorMessage ? (
                            <span className="text-error">{ci.errorMessage}</span>
                          ) : ci.verifiedAt ? (
                            <span className="text-text-muted text-xs">
                              Verified {new Date(ci.verifiedAt).toLocaleTimeString()}
                            </span>
                          ) : (
                            <span className="text-text-muted">—</span>
                          )}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableContainer>
            )}
          </div>
        </CardBody>
      </Card>

      <ActionReasonModal
        isOpen={pauseModalOpen}
        title="Pause Rollout Campaign"
        actionLabel="Pause Rollout"
        onClose={() => setPauseModalOpen(false)}
        onSubmit={handlePause}
      />

      <ActionReasonModal
        isOpen={rollbackModalOpen}
        title="Emergency Campaign Rollback"
        actionLabel="Confirm Rollback"
        onClose={() => setRollbackModalOpen(false)}
        onSubmit={handleRollback}
      />
    </div>
  );
}
